import { TaskEffortBadge } from '@/components/badges/TaskEffortBadge';
import IdWithCopy from '@/components/IdWithCopy';
import { JsonViewer } from '@/components/JsonViewer';
import { MetadataItem } from '@/components/MetadataItem';
import { TabNav } from '@/components/TabNav';
import RockDocRenderer from '@/components/docs/RockDocRenderer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { taskDocState, taskTypeState } from '@/state';
import { useLoadableAtomValue } from '@/state/loadableHooks';
import type { ConsiderationType, TaskType, TaskTypeId } from '@rock/types';
import { ArrowLeft } from 'lucide-react';
import type { FC } from 'react';
import { useMemo } from 'react';
import { Link, Navigate, Route, Routes, useNavigate, useParams } from 'react-router-dom';
import { HowOftenCard } from './HowOftenCard';
import { TaskTypeScreenConfigTab } from './TaskTypeScreenConfigTab';
import { TaskTypeAiResultsPage } from './ai-results/TaskTypeAiResultsPage';

export const TaskTypeDetailsPage: FC<{ considerationType: ConsiderationType }> = ({ considerationType }) => {
    const { taskTypeId } = useParams<{ taskTypeId?: TaskTypeId }>();
    const [taskType, taskTypeLoadable] = useLoadableAtomValue(taskTypeState(taskTypeId));
    const navigate = useNavigate();

    const basePath = `/content/considerations/${considerationType.id}/tasks/${taskTypeId}`;

    if (taskTypeLoadable.state === 'loading') {
        return (
            <Card>
                <CardContent className="p-6">
                    <div className="text-muted-foreground">Loading task…</div>
                </CardContent>
            </Card>
        );
    }

    if (!taskType) {
        return (
            <Card>
                <CardContent className="p-6">
                    <div className="text-muted-foreground">Task not found.</div>
                </CardContent>
            </Card>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-3">
                <Button variant="ghost" size="icon" onClick={() => navigate(-1)} aria-label="Back to tasks">
                    <ArrowLeft className="h-4 w-4" />
                </Button>
                <div className="min-w-0">
                    <div className="text-xl font-semibold">Task: {taskType.title}</div>
                    <IdWithCopy id={taskType.id} variant="muted" className="text-sm" showLabel />
                </div>
            </div>
            <TabNav
                tabs={[
                    { label: 'Details', to: `${basePath}/details` },
                    { label: 'Screen Config', to: `${basePath}/screen-config` },
                    { label: 'AI Results', to: `${basePath}/ai-results` },
                ]}
            />

            <Routes>
                <Route index element={<Navigate replace to="./details" />} />
                <Route
                    path="details"
                    element={<TaskTypeDetailsTab taskType={taskType} considerationType={considerationType} />}
                />
                <Route
                    path="screen-config"
                    element={<TaskTypeScreenConfigTab taskType={taskType} spaceId={considerationType.spaceId} />}
                />
                <Route
                    path="ai-results/*"
                    element={<TaskTypeAiResultsPage taskType={taskType} considerationType={considerationType} />}
                />
            </Routes>
        </div>
    );
};

const TaskTypeDetailsTab: FC<{ taskType: TaskType; considerationType: ConsiderationType }> = ({
    taskType,
    considerationType,
}) => {
    const taskDocId = taskType.taskDocId;
    const [taskDoc, taskDocLoadable] = useLoadableAtomValue(useMemo(() => taskDocState(taskDocId), [taskDocId]));
    const taskDocError = taskDocLoadable.state === 'hasError' ? taskDocLoadable.error : undefined;
    const isTaskDocLoading = taskDocId ? taskDocLoadable.state === 'loading' : false;

    return (
        <div className="space-y-6">
            <Card>
                <CardHeader className="border-b-1 pb-3">
                    <CardTitle className="text-xl">Metadata</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="space-y-5">
                        <div className="grid gap-5 md:grid-cols-2">
                            <MetadataItem
                                label="Consideration Type"
                                value={
                                    <Link
                                        to={`/content/considerations/${considerationType.id}/details`}
                                        className="text-sky-500 hover:underline"
                                    >
                                        {considerationType.title}
                                    </Link>
                                }
                            />
                            <MetadataItem label="Effort" value={<TaskEffortBadge effort={taskType.effort} />} />
                        </div>
                    </div>
                </CardContent>
            </Card>

            <HowOftenCard taskType={taskType} />

            <Card>
                <CardHeader className="flex flex-col gap-2 border-b-1 pb-3">
                    <CardTitle className="text-xl">Content</CardTitle>
                    {!taskDocId ? (
                        <div className="section-description">This type is not linked to a Task Doc yet.</div>
                    ) : null}
                    {isTaskDocLoading ? <div className="section-description">Loading Doc…</div> : null}
                    {taskDocError ? (
                        <div className="text-sm text-destructive">
                            Unable to load Doc:{' '}
                            {taskDocError instanceof Error ? taskDocError.message : `${taskDocError}`}
                        </div>
                    ) : null}
                </CardHeader>
                <CardContent>
                    {taskDoc ? (
                        <div className="space-y-4">
                            <div className="space-y-1">
                                <h3 className="text-lg font-medium text-card-foreground">{taskDoc.title}</h3>
                                <div className="section-description">
                                    Last synced {new Date(taskDoc.updatedAt).toLocaleString()}
                                </div>
                            </div>
                            <RockDocRenderer blocks={taskDoc.blocks} />
                        </div>
                    ) : !taskDocId ? (
                        <div className="section-description">
                            Link a Task Doc in the metadata section to display its content here.
                        </div>
                    ) : isTaskDocLoading ? null : (
                        <div className="section-description">No Doc content available for this type.</div>
                    )}
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle>Task Type Object</CardTitle>
                    <CardDescription>The complete task type payload stored in Firestore.</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="max-h-[480px] overflow-auto rounded-md bg-muted p-4">
                        <JsonViewer data={taskType} />
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};
